import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

export interface NotificationData {
  user_id: string;
  title: string;
  message: string;
  type?: 'system' | 'order' | 'inventory' | 'task' | 'transfer' | 'rental' | 'reminder';
  priority?: 'low' | 'medium' | 'high';
  entity_type?: string;
  entity_id?: string;
  data?: unknown;
}

export interface EmailData {
  to: string;
  subject: string;
  html?: string;
  text?: string;
  template?: string;
  templateData?: Record<string, unknown>;
}

export interface SMSData {
  to: string;
  message: string;
}

export interface PushNotificationData {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: Record<string, unknown>;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

export interface NotificationResult {
  success: boolean;
  messageId?: string;
  error?: string;
  errorCode?: string;
}

export class NotificationService {
  static async sendNotification(message: string, type: string = 'info'): Promise<void> {
    const logContext = { message, type };
    logger.info('NotificationService: Sending toast notification', logContext);
    
    // Показываем toast уведомление
    switch (type) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      case 'info':
      default:
        toast.info(message);
        break;
    }
  }
  
  static async sendSMS(smsData: SMSData): Promise<NotificationResult> {
    const logContext = { to: smsData.to, messageLength: smsData.message.length };
    
    try {
      logger.info('NotificationService: Sending SMS', logContext);
      
      // Валидация входных данных
      if (!smsData.to || !smsData.message) {
        const error = 'Phone number and message are required';
        logger.error('NotificationService: SMS validation failed', { ...logContext, error });
        return { success: false, error, errorCode: 'VALIDATION_ERROR' };
      }

      // Нормализация номера телефона
      const normalizedPhone = this.normalizePhoneNumber(smsData.to);
      if (!normalizedPhone) {
        const error = 'Invalid phone number format';
        logger.error('NotificationService: Invalid phone number', { ...logContext, error, phone: smsData.to });
        return { success: false, error, errorCode: 'INVALID_PHONE' };
      }

      // Отправка через Supabase Edge Function
      const { data, error } = await supabaseWorker.functions.invoke('send-sms', {
        body: { 
          phone: normalizedPhone, 
          message: smsData.message,
          timestamp: new Date().toISOString()
        }
      });
      
      if (error) {
        logger.error('NotificationService: SMS sending failed', { ...logContext, error: error.message });
        toast.error('Не удалось отправить SMS');
        return { 
          success: false, 
          error: error.message || 'SMS sending failed',
          errorCode: 'SMS_SEND_ERROR'
        };
      }
      
      logger.info('NotificationService: SMS sent successfully', { ...logContext, messageId: data?.messageId });
      toast.success('SMS отправлено');
      return { 
        success: true, 
        messageId: data?.messageId 
      };
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('NotificationService: SMS sending error', { ...logContext, error: errorMessage });
      toast.error('Ошибка отправки SMS');
      return { 
        success: false, 
        error: errorMessage,
        errorCode: 'UNEXPECTED_ERROR'
      };
    }
  }
  
  static async sendEmail(emailData: EmailData): Promise<NotificationResult> {
    const logContext = { to: emailData.to, subject: emailData.subject, hasTemplate: !!emailData.template };
    
    try {
      logger.info('NotificationService: Sending email', logContext);
      
      // Валидация входных данных
      if (!emailData.to || !emailData.subject) {
        const error = 'Email address and subject are required';
        logger.error('NotificationService: Email validation failed', { ...logContext, error });
        return { success: false, error, errorCode: 'VALIDATION_ERROR' };
      }

      if (!emailData.html && !emailData.text && !emailData.template) {
        const error = 'Email content (html, text, or template) is required';
        logger.error('NotificationService: Email content validation failed', { ...logContext, error });
        return { success: false, error, errorCode: 'VALIDATION_ERROR' };
      }

      // Валидация email адреса
      if (!this.isValidEmail(emailData.to)) {
        const error = 'Invalid email address format';
        logger.error('NotificationService: Invalid email format', { ...logContext, error });
        return { success: false, error, errorCode: 'INVALID_EMAIL' };
      }

      // Отправка через Supabase Edge Function
      const { data, error } = await supabaseWorker.functions.invoke('send-email', {
        body: {
          to: emailData.to,
          subject: emailData.subject,
          html: emailData.html,
          text: emailData.text,
          template: emailData.template,
          templateData: emailData.templateData,
          timestamp: new Date().toISOString()
        }
      });
      
      if (error) {
        logger.error('NotificationService: Email sending failed', { ...logContext, error: error.message });
        toast.error('Не удалось отправить email');
        return { 
          success: false, 
          error: error.message || 'Email sending failed',
          errorCode: 'EMAIL_SEND_ERROR'
        };
      }
      
      logger.info('NotificationService: Email sent successfully', { ...logContext, messageId: data?.messageId });
      toast.success('Email отправлен');
      return { 
        success: true, 
        messageId: data?.messageId 
      };
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('NotificationService: Email sending error', { ...logContext, error: errorMessage });
      toast.error('Ошибка отправки email');
      return { 
        success: false, 
        error: errorMessage,
        errorCode: 'UNEXPECTED_ERROR'
      };
    }
  }

  static async createReminderNotification(
    userId: string, 
    title: string, 
    message: string, 
    priority: string = 'medium'
  ) {
    console.log(`[NotificationService] Reminder for ${userId}: ${title} - ${message} (${priority})`);
    
    const notificationData: NotificationData = {
      user_id: userId,
      title,
      message,
      type: 'reminder',
      priority: priority as 'low' | 'medium' | 'high'
    };
    
    return this.createNotification(notificationData);
  }

  static async createTaskNotification(
    userId: string, 
    title: string, 
    message: string, 
    taskId: string, 
    priority: string = 'medium'
  ) {
    console.log(`[NotificationService] Task notification for ${userId}: ${title} - ${message} (task: ${taskId}, priority: ${priority})`);
    
    const notificationData: NotificationData = {
      user_id: userId,
      title,
      message,
      type: 'task',
      priority: priority as 'low' | 'medium' | 'high',
      entity_type: 'task',
      entity_id: taskId
    };
    
    return this.createNotification(notificationData);
  }

  static async createNotification(data: NotificationData): Promise<NotificationResult> {
    const logContext = { userId: data.user_id, type: data.type, priority: data.priority };
    
    try {
      logger.info('NotificationService: Creating notification', logContext);
      
      // Валидация входных данных
      if (!data.user_id || !data.title || !data.message) {
        const error = 'User ID, title, and message are required';
        logger.error('NotificationService: Notification validation failed', { ...logContext, error });
        return { success: false, error, errorCode: 'VALIDATION_ERROR' };
      }

      const { data: result, error } = await supabaseWorker
        .from('notifications')
        .insert({
          user_id: data.user_id,
          title: data.title,
          message: data.message,
          type: data.type || 'system',
          priority: data.priority || 'medium',
          entity_type: data.entity_type,
          entity_id: data.entity_id,
          data: data.data,
          read: false
        })
        .select('id')
        .single();
      
      if (error) {
        const errorMessage = (typeof error === 'object' && error && 'message' in error)
          ? String((error as { message?: unknown }).message)
          : 'Database error creating notification';
        logger.error('NotificationService: Database error creating notification', { ...logContext, error: errorMessage });
        toast.error('Не удалось создать уведомление');
        return { 
          success: false, 
          error: errorMessage,
          errorCode: 'DATABASE_ERROR'
        };
      }
      
      // Показываем toast уведомление
      await this.sendNotification(data.message, 'info');
      
      const createdIdUnknown = (result as { id?: unknown } | null)?.id;
      const createdId = typeof createdIdUnknown === 'string' ? createdIdUnknown : undefined;
      logger.info('NotificationService: Notification created successfully', { ...logContext, notificationId: createdId });
      return { 
        success: true, 
        messageId: createdId 
      };
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('NotificationService: Notification creation error', { ...logContext, error: errorMessage });
      toast.error('Ошибка создания уведомления');
      return { 
        success: false, 
        error: errorMessage,
        errorCode: 'UNEXPECTED_ERROR'
      };
    }
  }

  static async markAsRead(notificationId: string): Promise<NotificationResult> {
    const logContext = { notificationId };
    
    try {
      logger.info('NotificationService: Marking notification as read', logContext);
      
      if (!notificationId) {
        const error = 'Notification ID is required';
        logger.error('NotificationService: Mark as read validation failed', { ...logContext, error });
        return { success: false, error, errorCode: 'VALIDATION_ERROR' };
      }

      const { error } = await supabaseWorker
        .from('notifications')
        .update({ 
          read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('id', notificationId);
      
      if (error) {
        const errorMessage = (typeof error === 'object' && error && 'message' in error)
          ? String((error as { message?: unknown }).message)
          : 'Database error marking notification as read';
        logger.error('NotificationService: Database error marking notification as read', { ...logContext, error: errorMessage });
        return { 
          success: false, 
          error: errorMessage,
          errorCode: 'DATABASE_ERROR'
        };
      }
      
      logger.info('NotificationService: Notification marked as read successfully', logContext);
      return { success: true };
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('NotificationService: Error marking notification as read', { ...logContext, error: errorMessage });
      return { 
        success: false, 
        error: errorMessage,
        errorCode: 'UNEXPECTED_ERROR'
      };
    }
  }

  // Новые методы для push-уведомлений
  static async sendPushNotification(
    userId: string, 
    pushData: PushNotificationData
  ): Promise<NotificationResult> {
    const logContext = { userId, title: pushData.title };
    
    try {
      logger.info('NotificationService: Sending push notification', logContext);
      
      // Валидация входных данных
      if (!userId || !pushData.title || !pushData.body) {
        const error = 'User ID, title, and body are required for push notifications';
        logger.error('NotificationService: Push notification validation failed', { ...logContext, error });
        return { success: false, error, errorCode: 'VALIDATION_ERROR' };
      }

      // Проверяем поддержку push-уведомлений в браузере
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        const error = 'Push notifications are not supported in this browser';
        logger.warn('NotificationService: Push notifications not supported', logContext);
        return { success: false, error, errorCode: 'NOT_SUPPORTED' };
      }

      // Отправляем через Supabase Edge Function для обработки push-уведомлений
      const { data, error } = await supabaseWorker.functions.invoke('send-push-notification', {
        body: {
          userId,
          title: pushData.title,
          body: pushData.body,
          icon: pushData.icon,
          badge: pushData.badge,
          data: pushData.data,
          actions: pushData.actions,
          timestamp: new Date().toISOString()
        }
      });
      
      if (error) {
        logger.error('NotificationService: Push notification sending failed', { ...logContext, error: error.message });
        return { 
          success: false, 
          error: error.message || 'Push notification sending failed',
          errorCode: 'PUSH_SEND_ERROR'
        };
      }
      
      logger.info('NotificationService: Push notification sent successfully', { ...logContext, messageId: data?.messageId });
      return { 
        success: true, 
        messageId: data?.messageId 
      };
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('NotificationService: Push notification error', { ...logContext, error: errorMessage });
      return { 
        success: false, 
        error: errorMessage,
        errorCode: 'UNEXPECTED_ERROR'
      };
    }
  }

  // Вспомогательные методы для валидации
  private static normalizePhoneNumber(phone: string): string | null {
    // Удаляем все символы кроме цифр и плюса
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    // Проверяем базовый формат (минимум 10 цифр)
    if (cleaned.length < 10) {
      return null;
    }
    
    // Добавляем + если его нет и номер начинается с 7 или 8
    if (!cleaned.startsWith('+')) {
      if (cleaned.startsWith('8')) {
        return '+7' + cleaned.substring(1);
      } else if (cleaned.startsWith('7')) {
        return '+' + cleaned;
      } else {
        return '+' + cleaned;
      }
    }
    
    return cleaned;
  }

  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Метод для отправки комплексных уведомлений (email + push + in-app)
  static async sendMultiChannelNotification(
    userId: string,
    title: string,
    message: string,
    options: {
      email?: string;
      phone?: string;
      sendPush?: boolean;
      sendEmail?: boolean;
      sendSMS?: boolean;
      emailTemplate?: string;
      emailTemplateData?: Record<string, unknown>;
      pushData?: Partial<PushNotificationData>;
    } = {}
  ): Promise<{
    inApp: NotificationResult;
    email?: NotificationResult;
    sms?: NotificationResult;
    push?: NotificationResult;
  }> {
    const logContext = { userId, title, channels: [] as string[] };
    
    if (options.sendEmail && options.email) logContext.channels.push('email');
    if (options.sendSMS && options.phone) logContext.channels.push('sms');
    if (options.sendPush) logContext.channels.push('push');
    logContext.channels.push('in-app');
    
    logger.info('NotificationService: Sending multi-channel notification', logContext);
    
    const results: {
      inApp: NotificationResult;
      email?: NotificationResult;
      sms?: NotificationResult;
      push?: NotificationResult;
    } = { inApp: { success: false } };
    
    // Создаем in-app уведомление
    results.inApp = await this.createNotification({
      user_id: userId,
      title,
      message,
      type: 'system',
      priority: 'medium'
    });
    
    // Отправляем email если указан
    if (options.sendEmail && options.email) {
      results.email = await this.sendEmail({
        to: options.email,
        subject: title,
        text: message,
        template: options.emailTemplate,
        templateData: options.emailTemplateData
      });
    }
    
    // Отправляем SMS если указан
    if (options.sendSMS && options.phone) {
      results.sms = await this.sendSMS({
        to: options.phone,
        message: `${title}: ${message}`
      });
    }
    
    // Отправляем push-уведомление если указано
    if (options.sendPush) {
      results.push = await this.sendPushNotification(userId, {
        title,
        body: message,
        ...options.pushData
      });
    }
    
    logger.info('NotificationService: Multi-channel notification completed', {
      ...logContext,
      results: (['inApp','email','sms','push'] as const)
        .filter((key) => typeof (results as Record<string, unknown>)[key] !== 'undefined')
        .map((key) => ({
          channel: key,
          success: (results as Record<string, NotificationResult | undefined>)[key]?.success === true,
        }))
    });
    
    return results;
  }
}

export default NotificationService;
